import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, Layers, BarChart3 } from "lucide-react";
import Layout from "src/components/layout/website/Layout";
import Button from "src/components/ui/Button";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Layout
      meta={{
        title: "Invyntra — Inventory Management for Growing Businesses",
        description:
          "Track stock, purchases, and sales in one dashboard. Invyntra is a straightforward inventory management platform built for small and mid-sized businesses. Start free.",
      }}
    >
      <div className="min-h-screen bg-white text-gray-800">
        {/* Hero */}
        <section className="text-center py-24 px-6 bg-gradient-to-b from-indigo-50 to-white">
          <h1 className="text-5xl font-bold mb-4 text-indigo-600">
            Inventory Management, Without the Overhead
          </h1>
          <p className="text-lg mb-8 text-gray-600 max-w-2xl mx-auto">
            Invyntra helps small and growing businesses track stock,
            purchases, and sales in one dashboard — no spreadsheets, no
            complex setup. Start free, no credit card required.
          </p>
          <Button onClick={() => navigate("/pricing")}>Get Started</Button>

          <div className="mt-14 max-w-5xl mx-auto">
            <img
              src="/assets/screenshots/dashboard.webp"
              alt="Invyntra inventory management dashboard showing total products, stock value, low stock alerts, and sales overview"
              className="w-full rounded-xl shadow-2xl border border-gray-200"
              width={1600}
              height={864}
              loading="eager"
            />
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                title: "Real-Time Tracking",
                desc: "Monitor stock levels, sales, and purchase movements as they happen, from one dashboard.",
                Icon: Activity,
              },
              {
                title: "Multi-Branch Ready",
                desc: "Run inventory for multiple branches or teams with role-based access, built in from day one.",
                Icon: Layers,
              },
              {
                title: "Clear Reports",
                desc: "See daily, monthly, or custom reports on stock and sales, and export them to PDF when you need to.",
                Icon: BarChart3,
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition"
              >
                <f.Icon className="h-10 w-10 mx-auto mb-4 text-indigo-600" />
                <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Product screenshots */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-3">
                Every workflow, in one place
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                From sales orders to team roles, Invyntra covers the daily
                operations of running a growing business — not just a stock
                count.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Sales orders",
                  desc: "Track every order from draft to completed, with payment status at a glance.",
                  img: "/assets/screenshots/sales.webp",
                  alt: "Sales orders list in Invyntra showing customer, amount, payment status, and order status",
                },
                {
                  title: "Purchase orders",
                  desc: "Manage supplier purchases and keep stock replenishment on schedule.",
                  img: "/assets/screenshots/purchases.webp",
                  alt: "Purchase orders list in Invyntra showing supplier, grand total, and payment status",
                },
                {
                  title: "Team roles & access",
                  desc: "Invite your team and control what each role — Admin, Manager, or Staff — can see and do.",
                  img: "/assets/screenshots/team.webp",
                  alt: "Team management page in Invyntra showing users with Admin, Manager, and Staff roles",
                },
                {
                  title: "Inventory activity",
                  desc: "Every stock movement — purchase, sale, or adjustment — logged with a clear audit trail.",
                  img: "/assets/screenshots/inventory.webp",
                  alt: "Inventory activity ledger in Invyntra showing stock in and stock out transactions per product",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition"
                >
                  <img
                    src={s.img}
                    alt={s.alt}
                    className="w-full border-b border-gray-200"
                    loading="lazy"
                  />
                  <div className="p-5">
                    <h3 className="font-semibold text-lg mb-1">{s.title}</h3>
                    <p className="text-gray-600 text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-20">
          <h2 className="text-3xl font-bold mb-4">
            Ready to take control of your inventory?
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8">
            Get a free 15-day trial set up for your business — no credit
            card, no long onboarding. We'll walk you through it.
          </p>
          <Link
            to="/contact"
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Request a Demo
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
